// src/pages/Doubts.tsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box, Typography, Button, Paper, CircularProgress, Stack, Avatar,
    Chip, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
    Dialog, DialogContent, DialogActions, TextField, Snackbar, Alert,
    InputAdornment, Divider, Badge,
} from "@mui/material";
import {
    Clear as ClearIcon, Reply as ReplyIcon,
    CheckCircle as AnsweredIcon, Help as OpenIcon,
    AttachFile as AttachFileIcon, Search as SearchIcon,
    FilterList as FilterIcon, Refresh as RefreshIcon,
    QuestionAnswer as DoubtsIcon, Schedule as TimeIcon,
    Person as PersonIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import { getClasses, getMediums, getSubjects } from "../services/subjectService";
import { getDoubts, answerDoubt, Doubt, getFullUrl } from "../services/doubtService";
import { CardShimmer } from "../components/Shimmer";

const PRIMARY = "#174143";
const ACCENT = "#3d9ea4";

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(14px); }
  to   { opacity:1; transform:translateY(0); }
`;

function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function getInitials(name: string): string {
    return name.split(" ").map(p => p[0]).join("").toUpperCase().slice(0, 2);
}

const USER_COLORS = [
    "linear-gradient(135deg,#667eea,#764ba2)",
    "linear-gradient(135deg,#4facfe,#00f2fe)",
    "linear-gradient(135deg,#43e97b,#38f9d7)",
    "linear-gradient(135deg,#f093fb,#f5576c)",
    "linear-gradient(135deg,#fa709a,#fee140)",
];

export default function DoubtsPage(): React.ReactElement {
    const [searchParams, setSearchParams] = useSearchParams();

    const [classes, setClasses] = useState<any[]>([]);
    const [mediums, setMediums] = useState<any[]>([]);
    const [subjects, setSubjects] = useState<any[]>([]);
    const [doubts, setDoubts] = useState<Doubt[]>([]);

    const [loading, setLoading] = useState(false);
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");

    // Answer dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDoubt, setSelectedDoubt] = useState<Doubt | null>(null);
    const [answerText, setAnswerText] = useState("");
    const [answerFile, setAnswerFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [snackbar, setSnackbar] = useState<{ severity: "success" | "error"; message: string } | null>(null);

    // URL filters
    const selectedClassId = searchParams.get("classId") || "";
    const selectedMediumId = searchParams.get("mediumId") || "";
    const selectedSubjectId = searchParams.get("subjectId") || "";
    const selectedStatus = searchParams.get("status") || "OPEN";

    const updateFilter = (key: string, value: string) => {
        const params = new URLSearchParams(searchParams.toString());
        if (value) params.set(key, value); else params.delete(key);
        if (key === "classId" || key === "mediumId") params.delete("subjectId");
        setSearchParams(params);
    };
    const clearFilters = () => setSearchParams({});

    // ── Metadata ──────────────────────────────────────────
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoadingMeta(true);
                const [cls, md] = await Promise.all([getClasses(), getMediums()]);
                if (mounted) {
                    setClasses(Array.isArray(cls) ? cls : []);
                    setMediums(Array.isArray(md) ? md : []);
                }
            } catch (e) { console.error("meta failed", e); }
            finally { if (mounted) setLoadingMeta(false); }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const subs = await getSubjects({
                    classId: selectedClassId ? Number(selectedClassId) : undefined,
                    mediumId: selectedMediumId ? Number(selectedMediumId) : undefined,
                });
                if (mounted) setSubjects(Array.isArray(subs) ? subs : []);
            } catch { if (mounted) setSubjects([]); }
        })();
        return () => { mounted = false; };
    }, [selectedClassId, selectedMediumId]);

    // ── Fetch doubts ─────────────────────────────────────
    const fetchDoubts = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            let answered: boolean | undefined;
            if (selectedStatus === "ANSWERED") answered = true;
            else if (selectedStatus === "OPEN") answered = false;

            const data = await getDoubts({
                subjectId: selectedSubjectId ? Number(selectedSubjectId) : undefined,
                answered,
                page: 0, size: 100,
            });
            setDoubts(Array.isArray(data) ? data : (data?.content ?? []));
        } catch (err: any) {
            setError("Failed to load doubts.");
        } finally { setLoading(false); }
    }, [selectedSubjectId, selectedStatus]);

    useEffect(() => { fetchDoubts(); }, [fetchDoubts]);

    // ── Client-side search ────────────────────────────────
    const filtered = useMemo(() => {
        if (!search.trim()) return doubts;
        const q = search.toLowerCase();
        return doubts.filter(d =>
            d.question.toLowerCase().includes(q) ||
            d.user?.displayName?.toLowerCase().includes(q) ||
            d.user?.district?.toLowerCase().includes(q)
        );
    }, [doubts, search]);

    // ── Counts ────────────────────────────────────────────
    const openCount = doubts.filter(d => d.status === "OPEN").length;
    const answeredCount = doubts.filter(d => d.status === "ANSWERED").length;

    // ── Handlers ──────────────────────────────────────────
    const handleOpenAnswer = (doubt: Doubt) => {
        setSelectedDoubt(doubt);
        setAnswerText(doubt.answer || "");
        setAnswerFile(null);
        setOpenDialog(true);
    };

    const submitAnswer = async () => {
        if (!selectedDoubt) return;
        if (!answerText.trim() && !answerFile) {
            setSnackbar({ severity: "error", message: "Please provide an answer text or attach a file." });
            return;
        }
        setSubmitting(true);
        try {
            await answerDoubt(selectedDoubt.id, answerText, answerFile);
            setSnackbar({ severity: "success", message: "Answer submitted successfully!" });
            setOpenDialog(false);
            fetchDoubts();
        } catch (err: any) {
            setSnackbar({ severity: "error", message: err.message || "Failed to submit answer." });
        } finally { setSubmitting(false); }
    };

    // ── Media renderer ────────────────────────────────────
    const renderMedia = (urls: string[]) => {
        if (!urls?.length) return null;
        return (
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
                {urls.map((url, idx) => {
                    const full = getFullUrl(url);
                    const isAudio = /\.(mp3|m4a|wav|ogg)$/i.test(url);
                    if (isAudio) return (
                        <Box key={idx} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 0.75, background: "#f8fafc" }}>
                            <audio controls src={full} style={{ height: 32, maxWidth: 220 }} />
                        </Box>
                    );
                    return (
                        <Box key={idx} component="img" src={full} alt="media"
                            sx={{ height: 90, width: 90, objectFit: "cover", borderRadius: 2, border: "1px solid", borderColor: "divider", cursor: "pointer", transition: "transform 0.15s", "&:hover": { transform: "scale(1.04)" } }}
                            onClick={() => window.open(full, "_blank")}
                        />
                    );
                })}
            </Stack>
        );
    };

    const hasFilters = !!(selectedClassId || selectedMediumId || selectedSubjectId || selectedStatus !== "OPEN");

    // ── Render ────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1000, mx: "auto" }}>

                {/* ── Hero Header ── */}
                <Paper elevation={0} sx={{
                    mb: 3, borderRadius: 4, overflow: "hidden",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                    color: "#fff", animation: `${fadeUp} 0.4s ease-out`, position: "relative",
                }}>
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -20, right: 120, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, position: "relative" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <DoubtsIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>Student Doubts</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
                                    Review and answer questions from students
                                </Typography>
                            </Box>
                        </Box>

                        <Stack direction="row" spacing={1.5} flexWrap="wrap">
                            <Chip
                                icon={<OpenIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
                                label={`${openCount} Open`}
                                sx={{ background: "rgba(251,191,36,0.3)", color: "#fff", fontWeight: 700, border: "1px solid rgba(251,191,36,0.5)" }}
                            />
                            <Chip
                                icon={<AnsweredIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
                                label={`${answeredCount} Answered`}
                                sx={{ background: "rgba(67,233,123,0.25)", color: "#fff", fontWeight: 700, border: "1px solid rgba(67,233,123,0.4)" }}
                            />
                        </Stack>
                    </Box>
                </Paper>

                {/* ── Filter + Search Bar ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 2, mb: 2.5, animation: `${fadeUp} 0.4s ease-out 60ms both` }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", minWidth: 70 }}>
                            <FilterIcon fontSize="small" />
                            <Typography variant="body2" fontWeight={700}>Filters</Typography>
                        </Box>

                        {/* Status */}
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={selectedStatus} label="Status" onChange={e => updateFilter("status", e.target.value)}>
                                <MenuItem value="ALL">All</MenuItem>
                                <MenuItem value="OPEN">Open</MenuItem>
                                <MenuItem value="ANSWERED">Answered</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Class</InputLabel>
                            <Select value={selectedClassId} label="Class" onChange={e => updateFilter("classId", String(e.target.value))}>
                                <MenuItem value=""><em>All</em></MenuItem>
                                {classes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Medium</InputLabel>
                            <Select value={selectedMediumId} label="Medium" onChange={e => updateFilter("mediumId", String(e.target.value))}>
                                <MenuItem value=""><em>All</em></MenuItem>
                                {mediums.map(m => <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Subject</InputLabel>
                            <Select value={selectedSubjectId} label="Subject" onChange={e => updateFilter("subjectId", String(e.target.value))}>
                                <MenuItem value=""><em>All Subjects</em></MenuItem>
                                {subjects.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {hasFilters && (
                            <Tooltip title="Clear all filters">
                                <IconButton size="small" onClick={clearFilters}
                                    sx={{ color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", "&:hover": { background: "rgba(239,68,68,0.08)" } }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Box sx={{ flex: 1 }} />

                        <TextField
                            size="small"
                            placeholder="Search doubts…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
                            sx={{ minWidth: 200 }}
                        />

                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchDoubts} sx={{ border: "1px solid", borderColor: "divider" }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {search && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            Showing {filtered.length} of {doubts.length} doubts
                        </Typography>
                    )}
                </Paper>

                {/* ── Content ── */}
                {loading || loadingMeta ? (
                    <CardShimmer count={6} />
                ) : error ? (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                        <DoubtsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
                        <Typography color="error">⚠ {error}</Typography>
                        <Button onClick={fetchDoubts} sx={{ mt: 2 }}>Retry</Button>
                    </Box>
                ) : filtered.length === 0 ? (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", py: 10, textAlign: "center" }}>
                        <DoubtsIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" fontWeight={700}>
                            {search ? "No doubts match your search" : "No doubts found"}
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                            {search ? "Try a different search term" : "Try changing the filters above"}
                        </Typography>
                    </Paper>
                ) : (
                    <Stack spacing={2}>
                        {filtered.map((doubt, idx) => {
                            const isAnswered = doubt.status === "ANSWERED";
                            const name = doubt.user?.displayName || "Unknown";
                            const gradient = USER_COLORS[doubt.user?.id % USER_COLORS.length] ?? USER_COLORS[0];

                            return (
                                <Paper
                                    key={doubt.id}
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        border: "1px solid",
                                        borderColor: isAnswered ? "rgba(67,233,123,0.25)" : "divider",
                                        overflow: "hidden",
                                        animation: `${fadeUp} 0.4s ease-out ${idx * 40}ms both`,
                                        transition: "box-shadow 0.2s, transform 0.2s",
                                        "&:hover": { boxShadow: "0 6px 24px rgba(0,0,0,0.09)", transform: "translateY(-1px)" },
                                    }}
                                >
                                    {/* Left accent bar */}
                                    <Box sx={{ height: 3, background: isAnswered ? "linear-gradient(90deg, #43e97b, #38f9d7)" : `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})` }} />

                                    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>

                                            {/* User Avatar */}
                                            <Box sx={{ textAlign: "center", minWidth: 64, flexShrink: 0 }}>
                                                <Avatar
                                                    src={getFullUrl(doubt.user?.profilePhotoUrl)}
                                                    sx={{ width: 48, height: 48, mx: "auto", mb: 0.75, border: "2px solid #fff", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", background: gradient, fontSize: "0.8rem", fontWeight: 800 }}
                                                >
                                                    {!doubt.user?.profilePhotoUrl && getInitials(name)}
                                                </Avatar>
                                                <Typography variant="caption" display="block" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: "0.7rem", color: PRIMARY }}>
                                                    {name}
                                                </Typography>
                                                {doubt.user?.district && (
                                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem" }}>
                                                        {doubt.user.district}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Question body */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {/* Meta row */}
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <TimeIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                                                        <Typography variant="caption" color="text.disabled">{timeAgo(doubt.createdAt)}</Typography>
                                                        {doubt.user?.className && (
                                                            <>
                                                                <Typography variant="caption" color="text.disabled">·</Typography>
                                                                <Typography variant="caption" color="text.disabled">{doubt.user.className}</Typography>
                                                            </>
                                                        )}
                                                    </Stack>
                                                    <Chip
                                                        icon={isAnswered
                                                            ? <AnsweredIcon sx={{ fontSize: "14px !important" }} />
                                                            : <OpenIcon sx={{ fontSize: "14px !important" }} />}
                                                        label={isAnswered ? "Answered" : "Open"}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700, fontSize: "0.7rem",
                                                            background: isAnswered ? "#dcfce7" : "#fef3c7",
                                                            color: isAnswered ? "#166534" : "#92400e",
                                                            "& .MuiChip-icon": { color: isAnswered ? "#166534" : "#92400e" },
                                                        }}
                                                    />
                                                </Box>

                                                {/* Question text */}
                                                <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.5, color: "#1e293b", mb: 0.5 }}>
                                                    {doubt.question}
                                                </Typography>

                                                {/* Question media */}
                                                {renderMedia(doubt.questionMedia)}

                                                {/* Answer display */}
                                                {doubt.answer && (
                                                    <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: "linear-gradient(135deg, #f0fdf4, #dcfce7)", border: "1px solid #bbf7d0" }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                                                            <PersonIcon sx={{ fontSize: 14, color: "#166534" }} />
                                                            <Typography variant="caption" color="#166534" fontWeight={800}>Admin Answer</Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="#1e293b" sx={{ lineHeight: 1.6 }}>{doubt.answer}</Typography>
                                                        {renderMedia(doubt.answerMedia)}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        <Divider sx={{ mt: 2, mb: 1.5 }} />

                                        {/* Actions */}
                                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Button
                                                size="small"
                                                variant={isAnswered ? "outlined" : "contained"}
                                                startIcon={<ReplyIcon />}
                                                onClick={() => handleOpenAnswer(doubt)}
                                                sx={isAnswered
                                                    ? { borderColor: ACCENT, color: ACCENT, fontWeight: 700, borderRadius: 2, "&:hover": { background: "rgba(61,158,164,0.06)" } }
                                                    : { background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, borderRadius: 2, boxShadow: "0 3px 10px rgba(23,65,67,0.25)" }
                                                }
                                            >
                                                {isAnswered ? "Edit Answer" : "Answer Doubt"}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* ── Answer Dialog ── */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                {/* Gradient header */}
                <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, px: 3, py: 2.5, color: "#fff" }}>
                    <Typography variant="h6" fontWeight={800}>
                        {selectedDoubt?.status === "ANSWERED" ? "Edit Answer" : "Answer Doubt"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>From {selectedDoubt?.user?.displayName || "student"}</Typography>
                </Box>

                <DialogContent dividers sx={{ pt: 2.5 }}>
                    <Stack spacing={2.5}>
                        {/* Original question */}
                        <Box sx={{ p: 2, borderRadius: 2, background: "#f8fafc", border: "1px solid", borderColor: "divider" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>Student Question</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>{selectedDoubt?.question}</Typography>
                            {selectedDoubt?.questionMedia?.length ? renderMedia(selectedDoubt.questionMedia) : null}
                        </Box>

                        <TextField
                            autoFocus
                            label="Your Answer"
                            multiline rows={5}
                            fullWidth size="small"
                            value={answerText}
                            onChange={e => setAnswerText(e.target.value)}
                            placeholder="Type your explanation here…"
                        />

                        {/* File attachment */}
                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                                fullWidth
                                sx={{ justifyContent: "flex-start", fontWeight: 600, borderRadius: 2, textTransform: "none", borderColor: "divider", color: "text.secondary" }}
                            >
                                {answerFile ? answerFile.name : "Attach Image or File (optional)"}
                                <input type="file" hidden onChange={e => setAnswerFile(e.target.files?.[0] || null)} />
                            </Button>
                            {answerFile && (
                                <Button size="small" color="error" onClick={() => setAnswerFile(null)} sx={{ mt: 0.5, textTransform: "none" }}>
                                    Remove file
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={submitAnswer} disabled={submitting}
                        sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, px: 3, borderRadius: 2 }}>
                        {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit Answer"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity ?? "info"} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}